import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import orangeLogo from '../assets/orangelog.png';

const PUBLISHER_BENEFITS = [
  { icon: '◆', title: 'Monetize existing content', body: 'Studies show over 97% of readers who hit a subscription paywall leave without ever subscribing — that is revenue you will never see. An Article Credit gate captures those readers with a single low-friction purchase instead of sending them elsewhere.' },
  { icon: '◆', title: 'Works alongside your subscription', body: 'Your subscribers are already sold. PubSilo reaches everyone else — the occasional reader, the first-timer, the person who found you through a search but will not commit to a monthly plan. Article Credit purchases do not replace subscriptions; they monetize the audience you were already losing.' },
  { icon: '◆', title: 'Manual / Auto-pricing engine', body: 'Control your pricing strategy: set prices manually or set your own parameters to have each article price decrease based on publish date and/or purchase traffic.'},
  { icon: '◆', title: 'Live price widget', body: 'Drop a single line of code on your site and the current article price updates on your website automatically to match the price you set in PubSilo — no manual edits when pricing changes.' },
  { icon: '◆', title: 'Publish your way', body: 'Host your full article on PubSilo and let us handle everything — or keep readers on your own site and protect content with a secure credit-verified handshake that verifies the purchase before unlocking access. Run both models at once across different articles. Your content, your infrastructure, your call.' },
  { icon: '◆', title: 'Full team management', body: 'Add authors and editors, assign roles, and manage your full editorial workflow in one dashboard.' },
  { icon: '◆', title: 'Transparent monthly payouts', body: 'Detailed invoices break down every purchase, price point, and fee so you always know what you earned.' },
  { icon: '◆', title: 'Reach a built-in audience', body: 'Your content surfaces to readers brought to the platform from publishers and outlets across the country.' },
  { icon: '◆', title: 'You own everything', body: 'Full editorial independence. Your content, your voice, your brand — we are the platform, not the editor.' },
  { icon: '◆', title: 'No upfront cost — ever', body: 'We earn only when you earn. No setup fees, monthly charges, or minimums.' },
];

const READER_BENEFITS = [
  { icon: '◆', title: 'The stories that matter to you', body: 'Access premium coverage from publishers across every genre and geography — local papers, national outlets, niche newsletters, all on one platform.' },
  { icon: '◆', title: 'The cure for subscription overload', body: 'Streaming. Music. Software. News. The average household is already stretched across a dozen monthly charges. Found a publisher you enjoy but cannot justify adding another recurring bill? Article Credits let you read exactly what interests you — no commitment, no renewal, no cancellation guilt.' },
  { icon: '◆', title: 'One balance, every publisher', body: 'Your Article Credit balance works across every publisher on PubSilo — local papers, national outlets, niche newsletters, all of it. Only refill your balance when you want. No monthly subscription. Article Credits never expire, so there is no pressure to rush.' },
  { icon: '◆', title: 'Discover new publishers', body: 'Browse a curated directory of publishers across every genre — from hyperlocal news to national analysis to niche newsletters.' },
  { icon: '◆', title: 'Free Article Credits on signup', body: 'Every new account starts with free Article Credits — no payment info required to get started.' },
];

function BenefitItem({ icon, title, body, accent }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', gap: '14px', alignItems: 'flex-start',
        padding: '14px 16px', borderRadius: '8px',
        background: hov ? 'rgba(255,255,255,0.04)' : 'transparent',
        transition: 'background 0.18s',
        marginBottom: '4px',
      }}
    >
      <div style={{
        width: '28px', height: '28px', borderRadius: '6px',
        background: accent, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '10px', color: '#fff', fontWeight: '900', marginTop: '1px',
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '15px', fontWeight: '700', color: '#f0f4ff', lineHeight: '1.3', marginBottom: '4px', fontFamily: 'Georgia, serif' }}>
          {title}
        </div>
        <div style={{ fontSize: '13px', color: '#7a8fa8', lineHeight: '1.6', fontFamily: 'Arial, sans-serif' }}>
          {body}
        </div>
      </div>
    </div>
  );
}

export default function LandingPage7() {
  const navigate = useNavigate();

  return (
    <div style={{
      minHeight: '100vh',
      background: '#06090f',
      fontFamily: 'Arial, sans-serif',
      display: 'flex',
      flexDirection: 'column',
    }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '52px 24px 40px',
        background: 'linear-gradient(to bottom, #0d1320 0%, #06090f 100%)',
      }}>
        {/* Logo on red tile — matches navbar */}
        <div
          onClick={() => navigate('/')}
          style={{
            background: '#F41238', borderRadius: '12px',
            padding: '12px 28px', cursor: 'pointer',
            boxShadow: '0 8px 40px rgba(244,18,56,0.35)',
            marginBottom: '28px',
          }}
        >
          <img src={orangeLogo} alt="PubSilo" style={{ height: '52px', objectFit: 'contain', display: 'block' }} />
        </div>

        <div style={{ fontSize: '13px', fontWeight: '800', letterSpacing: '0.22em', textTransform: 'uppercase', color: '#4a6a8a', marginBottom: '14px', fontFamily: 'Arial, sans-serif' }}>
          Independent Publishing, Reimagined
        </div>

        <h1 style={{
          fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: '900',
          color: '#f0f4ff', lineHeight: '1.2', textAlign: 'center',
          margin: '0 0 16px', fontFamily: 'Georgia, serif',
          maxWidth: '720px',
        }}>
          Built for the people who create the news and the people who need it.
        </h1>

        <p style={{ fontSize: '16px', color: '#5a7a9a', textAlign: 'center', maxWidth: '520px', lineHeight: '1.7', margin: 0, fontFamily: 'Arial, sans-serif' }}>
          PubSilo is the marketplace where publishers of every size earn from their work — and readers pay only for what they actually read.
        </p>

        {/* Decorative divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '36px', width: '100%', maxWidth: '800px' }}>
          <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to right, transparent, #1e3a5a)' }} />
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F41238' }} />
          <div style={{ flex: 1, height: '1px', background: 'linear-gradient(to left, transparent, #1e3a5a)' }} />
        </div>
      </div>

      {/* ── Two-panel body ── */}
      <div style={{ flex: 1, display: 'flex', minHeight: '0' }}>

        {/* ── Left: Publishers ── */}
        <div style={{
          flex: 1, padding: '40px 40px 52px',
          borderRight: '1px solid #111d2e',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Panel label */}
          <div style={{ marginBottom: '28px' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'rgba(244,18,56,0.12)', border: '1px solid rgba(244,18,56,0.25)',
              borderRadius: '20px', padding: '5px 14px',
              fontSize: '11px', fontWeight: '900', letterSpacing: '0.14em', textTransform: 'uppercase',
              color: '#F41238', marginBottom: '16px',
            }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#F41238', display: 'inline-block' }} />
              For Publishers
            </div>
            <h2 style={{ fontSize: 'clamp(20px, 2.5vw, 30px)', fontWeight: '900', color: '#f0f4ff', margin: '0 0 8px', fontFamily: 'Georgia, serif', lineHeight: '1.25' }}>
              Grow your revenue.<br />Keep your voice.
            </h2>
            <p style={{ fontSize: '14px', color: '#4a6a8a', lineHeight: '1.65', margin: 0, fontFamily: 'Arial, sans-serif' }}>
              Everything a modern newsroom needs to publish independently and get paid fairly.
            </p>
          </div>

          {/* Benefits list */}
          <div style={{ flex: 1 }}>
            {PUBLISHER_BENEFITS.map((b, i) => (
              <BenefitItem key={i} {...b} accent="#c01030" />
            ))}
          </div>

          {/* CTA */}
          <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #111d2e' }}>
            <Link to="/company-contact" style={{
              display: 'inline-block', textDecoration: 'none',
              background: '#F41238', color: '#fff',
              padding: '13px 28px', borderRadius: '8px',
              fontSize: '14px', fontWeight: '800', letterSpacing: '0.03em',
              boxShadow: '0 4px 20px rgba(244,18,56,0.3)',
              transition: 'box-shadow 0.2s, transform 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 28px rgba(244,18,56,0.5)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(244,18,56,0.3)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Become a Publisher →
            </Link>
            <div style={{ fontSize: '12px', color: '#3a5570', marginTop: '10px', fontFamily: 'Arial, sans-serif' }}>
              No commitment required — we'll walk you through everything.
            </div>
          </div>
        </div>

        {/* ── Right: Readers ── */}
        <div style={{
          flex: 1, padding: '40px 40px 52px',
          display: 'flex', flexDirection: 'column',
          background: 'linear-gradient(135deg, #06090f 0%, #080d18 100%)',
        }}>
          {/* Panel label */}
          <div style={{ marginBottom: '28px' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)',
              borderRadius: '20px', padding: '5px 14px',
              fontSize: '11px', fontWeight: '900', letterSpacing: '0.14em', textTransform: 'uppercase',
              color: '#60a5fa', marginBottom: '16px',
            }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }} />
              For Readers
            </div>
            <h2 style={{ fontSize: 'clamp(20px, 2.5vw, 30px)', fontWeight: '900', color: '#f0f4ff', margin: '0 0 8px', fontFamily: 'Georgia, serif', lineHeight: '1.25' }}>
              Pay for what you read.<br />Nothing more.
            </h2>
            <p style={{ fontSize: '14px', color: '#4a6a8a', lineHeight: '1.65', margin: 0, fontFamily: 'Arial, sans-serif' }}>
              Real journalism from publishers of every size — content curated to your interests and location, no subscription traps.
            </p>
          </div>

          {/* Benefits list */}
          <div style={{ flex: 1 }}>
            {READER_BENEFITS.map((b, i) => (
              <BenefitItem key={i} {...b} accent="#1d4ed8" />
            ))}
          </div>

          {/* CTA */}
          <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #111d2e' }}>
            <Link to="/signup/reader" style={{
              display: 'inline-block', textDecoration: 'none',
              background: '#2563eb', color: '#fff',
              padding: '13px 28px', borderRadius: '8px',
              fontSize: '14px', fontWeight: '800', letterSpacing: '0.03em',
              boxShadow: '0 4px 20px rgba(37,99,235,0.3)',
              transition: 'box-shadow 0.2s, transform 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 28px rgba(37,99,235,0.5)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,99,235,0.3)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Create Free Account →
            </Link>
   
          </div>
        </div>
      </div>

      {/* ── Footer strip ── */}
      <div style={{
        borderTop: '1px solid #0e1a28',
        padding: '20px 40px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
        background: '#040710',
      }}>
        <div style={{ fontSize: '13px', color: '#2a4060', fontFamily: 'Arial, sans-serif' }}>
          © {new Date().getFullYear()} PubSilo, LLC
        </div>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          <Link to="/login" style={{ fontSize: '13px', color: '#3a6080', textDecoration: 'none', fontFamily: 'Arial, sans-serif' }}
            onMouseEnter={e => e.currentTarget.style.color = '#60a5fa'}
            onMouseLeave={e => e.currentTarget.style.color = '#3a6080'}
          >
            Already have an account? Log in
          </Link>
          <Link to="/" style={{ fontSize: '13px', color: '#3a6080', textDecoration: 'none', fontFamily: 'Arial, sans-serif' }}
            onMouseEnter={e => e.currentTarget.style.color = '#f0f4ff'}
            onMouseLeave={e => e.currentTarget.style.color = '#3a6080'}
          >
            ← Back to PubSilo
          </Link>
        </div>
      </div>
    </div>
  );
}
